import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { useSellerStore } from '@/store/seller';

export function MyStorePage() {
  const navigate = useNavigate();
  const { ToastContainer } = useToast();
  const { 
    salesMetrics, 
    businessStatus, 
    setSalesMetrics, 
    setBusinessStatus, 
    setLoading, 
    setError 
  } = useSellerStore();

  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const loadStoreData = async () => {
      try {
        setLoading(true);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }
        

        // 대시보드 데이터 가져오기
        const dashboardResult = await sellerService.getDashboardData(restaurantResult.restaurantId);
        if (dashboardResult.success && dashboardResult.data) {
          setSalesMetrics(dashboardResult.data.salesMetrics);
          setBusinessStatus(dashboardResult.data.businessStatus);
        }
        
      } catch (error) {
        console.error('Store data load error:', error);
        setError('매장 정보를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
        setLoadingData(false);
      }
    };

    loadStoreData();
  }, [setLoading, setError, setSalesMetrics, setBusinessStatus]);


  if (loadingData) {
    return (
      <SellerPageContainer activeNavId="store">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">매장 정보를 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="store">
      <div className="pb-4">
        {/* 헤더 */}
        <div className="mb-6">
          <h1 className="text-[24px] font-bold text-gray-800 text-center">내 가게</h1>
        </div>

        {/* 영업 상태 표시 */}
        <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex items-center justify-end">
            <div className={`w-3 h-3 rounded-full mr-2 ${businessStatus ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-lg font-medium">
              {businessStatus ? '영업 중' : '영업 종료'}
            </span>
          </div>
        </div>

        {/* 판매 관리 섹션 */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">판매 관리</h2>
          
          {/* 판매내역 */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">판매내역</h3>
                <p className="text-sm text-gray-600">매출 현황과 판매 내역을 확인하세요</p>
                {salesMetrics && (
                  <div className="mt-2 flex items-center space-x-4">
                    <span className="text-sm text-blue-600">
                      {salesMetrics.currentMonth} 매출: {salesMetrics.monthlyRevenue.toLocaleString()}원
                    </span>
                    <span className="text-sm text-green-600">
                      판매건수: {salesMetrics.monthlySalesCount}건
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/seller/store/sales-history')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                상세보기
              </button>
            </div>
          </div>

          {/* 마감할인 상품 */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">오늘의 마감할인</h3>
                <p className="text-sm text-gray-600">당일 마감할인 상품을 설정하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store/deadline-discount')}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                설정하기
              </button>
            </div>
          </div>

          {/* 예약할인 상품 */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">예약할인 상품</h3>
                <p className="text-sm text-gray-600">예약할인 상품을 설정하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store/reservation-discount')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                설정하기
              </button>
            </div>
          </div>
        </div>

        {/* 상품 관리 섹션 */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">상품 관리</h2>
          
          {/* 상품 등록 */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">상품 등록</h3>
                <p className="text-sm text-gray-600">새로운 상품을 등록하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store/product-register')}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                등록하기
              </button>
            </div>
          </div>

          {/* 상품 수정 */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">상품 수정</h3>
                <p className="text-sm text-gray-600">등록된 상품을 수정하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store/product-edit')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                수정하기
              </button>
            </div>
          </div>
        </div>

        {/* 가게 관리 섹션 */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">가게 관리</h2>
          
          {/* 가게 정보 수정 */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">가게 정보 수정</h3>
                <p className="text-sm text-gray-600">가게 기본 정보를 수정하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store-edit')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                수정하기
              </button>
            </div>
          </div>

          {/* 영업시간 설정 */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">영업시간 설정</h3>
                <p className="text-sm text-gray-600">요일별 영업시간을 설정하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/operating-hours')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                설정하기
              </button>
            </div>
          </div>

          {/* 리뷰 보기 */}
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">리뷰 보기</h3>
                <p className="text-sm text-gray-600">고객 리뷰를 확인하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store-reviews')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                보기
              </button>
            </div>
          </div>

          {/* 내 가게 모니터링 */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-800">내 가게 모니터링</h3>
                <p className="text-sm text-gray-600">소비자 관점에서 내 가게를 확인하세요</p>
              </div>
              <button
                onClick={() => navigate('/seller/store-monitoring')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                모니터링
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}